import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {UtilsService} from '../../core/utils/utils.service';
import {GenericFormService} from '../../core/services/generics/generic-form.service';
import {ParametresService} from '../../core/services/entities/parametres.service';
import {ParametreImageEnum} from '../../core/enums/parametre-image-enum';
import {MSG_KEY, MSG_SEVERITY} from '../../core/constants';
import {ToastService} from "../../core/services/technique/toast.service";
import {Subscription} from "rxjs";
import {ProduitsService} from "../../core/services/entities/produits.service";
import {ButtonThemeEnum} from '../../shared/ui/button/button-theme-enum';
import {confirm} from 'devextreme/ui/dialog';

@Component({
    selector: 'yo-parametres',
    templateUrl: './parametres.component.html',
    styleUrls: ['./parametres.component.scss'],
    standalone: false
})
export class ParametresComponent implements OnInit, OnDestroy {

  public readonly ButtonThemeEnum = ButtonThemeEnum;
  public readonly ParametreImageEnum = ParametreImageEnum;


  form: FormGroup;
  refresh = 1;
  subTauxDePerte: Subscription;
  useTauxDePerte: Boolean = false;

  /**
   * Aperçus locaux des fichiers choisis mais pas encore enregistrés, indexés par nom de contrôle du formulaire.
   * Sans eux, l'écran affichait l'image servie par le back — donc l'ancienne — juste à côté d'un champ qui
   * contenait déjà la nouvelle.
   */
  private previewUrlMap = new Map<string, string>();

  constructor(public utils: UtilsService,
              private gfs: GenericFormService,
              private parametresSvc: ParametresService,
              private toastSvc: ToastService,
              private produitsSvc: ProduitsService) {
  }

  initForm = () => {

    this.subTauxDePerte = this.produitsSvc.tauxDePerte().subscribe((result) => {
      this.useTauxDePerte = result.one;
    });

    this.form = new FormGroup({
      background: new FormControl(undefined),
      logo:new FormControl(undefined)
    });
  };

  ngOnInit() {
    this.initForm();
  }

  ngOnDestroy() {
    this.utils.unsubscribe(this.subTauxDePerte);
  }

  onChangeImage = (event, formControlName:string) => {
    if (event.target.files && event.target.files.length) {
      const file = event.target.files[0];
      this.form.controls[formControlName].setValue(file);

      // lecture en data URL, idiome déjà employé par les dialogues d'appellation, d'allergène et PMS.
      // URL.createObjectURL serait moins coûteux mais imposerait de révoquer chaque URL, y compris à la
      // destruction du composant, là où une data URL disparaît avec la chaîne.
      const reader = new FileReader();
      reader.onload = () => this.previewUrlMap.set(formControlName, reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  /**
   * Source de l'image affichée : l'aperçu local s'il y a un fichier en attente d'enregistrement, sinon l'image
   * servie par le back. `refresh` change après un enregistrement ou une suppression pour contourner le cache.
   *
   * Appelée à chaque cycle de détection de changements, sans dommage : elle renvoie une chaîne égale par valeur
   * d'un cycle à l'autre, et Angular compare avant d'écrire `src` — l'image n'est pas rechargée tant que
   * `refresh` ne change pas.
   */
  getImageSrc = (formControlName: string, parametreImage: ParametreImageEnum): string =>
    this.previewUrlMap.get(formControlName)
    ?? `dolrest/utils/parametre-image?imageName=${parametreImage}&date=${this.refresh}`;

  hasPreview = (formControlName: string): boolean => this.previewUrlMap.has(formControlName);

  save = () => {

    this.gfs.validateAllFormFields(this.form);

    if (this.form.valid) {
      this.parametresSvc.savePreferences(this.form.controls['background'].value, this.form.controls['logo'].value, this.useTauxDePerte).subscribe(() => {
        // les aperçus locaux sont abandonnés au profit de l'image servie par le back : on voit ainsi que
        // l'enregistrement a bien abouti, au lieu de continuer à regarder le fichier local
        this.previewUrlMap.clear();

        this.refresh = new Date().getTime();
        this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `Le fond d'écran et le logo ont été enregistrés avec succès. Veuillez recharger votre page en appuyant sur la touche F5 afin d'afficher les nouvelles modifications apportées`);
      });
    }

  };

  /**
   * Supprime l'image personnalisée et rétablit celle livrée par défaut — c'est la même opération : le back sert
   * l'image par défaut dès que le fichier personnalisé est absent.
   *
   * `fileInput` est l'élément natif du champ fichier : il conserve son fichier sélectionné après un enregistrement,
   * et le vider est le seul moyen de le remettre à zéro depuis le composant.
   */
  removeImage = async (parametreImage: ParametreImageEnum, formControlName: string, fileInput: HTMLInputElement): Promise<void> => {

    const confirmed: boolean = await confirm(
      `Êtes-vous sûr de vouloir supprimer cette image ? L'image par défaut sera de nouveau utilisée.`,
      'Suppression');

    if (!confirmed) {
      return;
    }

    this.parametresSvc.removeParametreImage(parametreImage).subscribe(() => {
      this.form.controls[formControlName].setValue(undefined);
      fileInput.value = '';
      this.previewUrlMap.delete(formControlName);
      this.refresh = new Date().getTime();
      this.toastSvc.displayToast(MSG_KEY.ROOT, MSG_SEVERITY.SUCCESS, `L'image a été supprimée, l'image par défaut est de nouveau utilisée. Veuillez recharger votre page en appuyant sur la touche F5 afin de l'appliquer partout`);
    });
  };


}
